import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toDateOnlyString } from '../utils/dateOnly';

const emptyDate = { startDate: null, endDate: null };

export const useTransactionFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get('date') || null;
  const initialFilters = {
    customerId: searchParams.get('customer_id') || '',
    supplierId: searchParams.get('supplier_id') || '',
    transactionType: searchParams.get('transaction_type') || '',
    keyword: searchParams.get('keyword') || '',
    date: { startDate: initialDate, endDate: initialDate },
  };
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const setFilter = useCallback((name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  }, []);

  const syncUrl = useCallback((filters) => {
    const next = new URLSearchParams();
    if (filters.customerId) next.set('customer_id', filters.customerId);
    if (filters.supplierId) next.set('supplier_id', filters.supplierId);
    if (filters.transactionType) next.set('transaction_type', filters.transactionType);
    if (filters.keyword) next.set('keyword', filters.keyword);
    if (filters.date?.endDate) {
      const date = toDateOnlyString(filters.date.endDate);
      next.set('date', date);
    }
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    setAppliedFilters(draftFilters);
    syncUrl(draftFilters);
  }, [draftFilters, syncUrl]);

  const applySingleFilter = useCallback((name, value) => {
    const nextFilters = { ...draftFilters, [name]: value };
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    syncUrl(nextFilters);
  }, [draftFilters, syncUrl]);

  const resetFilters = useCallback(() => {
    const emptyFilters = { customerId: '', supplierId: '', transactionType: '', keyword: '', date: emptyDate };
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const changePageSize = useCallback((value) => {
    setPageSize(value);
    setCurrentPage(1);
  }, []);

  return {
    draftFilters,
    appliedFilters,
    setFilter,
    applyFilters,
    applySingleFilter,
    resetFilters,
    currentPage,
    setCurrentPage,
    pageSize,
    changePageSize,
  };
};
